import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, RefreshCw, TriangleAlert } from 'lucide-react';
import { formatCurrency } from '@/lib/i18n';
import { Badge } from '@/components/ui/badge';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { formatDateTime } from '../lib';
import { warningSentence } from '../lib/preview';
import {
  useMenuVersionPreviewQuery,
  type MenuChangeKind,
  type MenuPreviewChange,
  type MenuPreviewSummary,
} from '../menuGovernanceApi';

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand';

const KIND_TONE: Record<MenuChangeKind, Tone> = {
  ADDED: 'success',
  WITHDRAWN: 'danger',
  REPRICED: 'info',
  CHANNELS_CHANGED: 'warning',
  REORDERED: 'neutral',
  UNCHANGED: 'neutral',
};

/**
 * Screen 4 — the conflict preview. What publishing this version would change,
 * per branch, before it changes: the screen that stops a bad Friday-lunch
 * publish, so it gets the most care of the six.
 *
 * Diffed as of the version's own `effectiveFrom` server-side, never as of now
 * (`publish-preview.ts`'s own doc) — a 06:00 publish is compared against what
 * will be true at 06:00, which may already include a 05:00 publish nobody has
 * looked at yet. That is stated on screen rather than left implicit, because
 * "before" quietly meaning something other than "right now" is exactly the
 * kind of mismatch this screen exists to catch in everything else.
 *
 * Available on `menu_governance.view` alone (`preview` is gated on `.view` on
 * the server, not `.publish`) — a branch manager who can never sign a version
 * must still be able to see what is coming.
 */
export default function ConflictPreview({ versionId, effectiveFrom }: { versionId: string; effectiveFrom: string | null }) {
  const { t, i18n } = useTranslation();
  const { data, isFetching, isError, refetch } = useMenuVersionPreviewQuery(versionId);

  const byBranch = useMemo(() => {
    const map = new Map<string, { branchName: string; changes: MenuPreviewChange[] }>();
    for (const change of data?.changes ?? []) {
      const bucket = map.get(change.branchId) ?? { branchName: change.branchName, changes: [] };
      bucket.changes.push(change);
      map.set(change.branchId, bucket);
    }
    return [...map.entries()];
  }, [data]);

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">{t('menuGovernance.preview.title')}</h3>
          <p className="text-xs text-ink-muted">
            {t('menuGovernance.preview.asOfHint', {
              time: effectiveFrom ? formatDateTime(effectiveFrom, i18n.language) : t('menuGovernance.publish.now'),
            })}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="me-1.5 h-3.5 w-3.5" />
          {t('menuGovernance.preview.refresh')}
        </Button>
      </div>

      {isFetching && !data ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState message={t('menuGovernance.preview.errorTitle')} onRetry={() => refetch()} />
      ) : !data || data.changes.length === 0 ? (
        <EmptyState title={t('menuGovernance.preview.emptyTitle')} hint={t('menuGovernance.preview.emptyHint')} />
      ) : (
        <>
          <SummaryCards summary={data.summary} t={t} />

          {byBranch.map(([branchId, bucket]) => (
            <div key={branchId} className="space-y-2">
              <h4 className="text-sm font-medium">{bucket.branchName}</h4>
              <TableWrap>
                <Table>
                  <THead>
                    <TR>
                      <TH>{t('menuGovernance.preview.columnItem')}</TH>
                      <TH>{t('menuGovernance.preview.columnChange')}</TH>
                      <TH>{t('menuGovernance.preview.columnBefore')}</TH>
                      <TH />
                      <TH>{t('menuGovernance.preview.columnAfter')}</TH>
                      <TH className="text-end">{t('menuGovernance.preview.columnMove')}</TH>
                    </TR>
                  </THead>
                  <tbody>
                    {bucket.changes.map((change) => (
                      <ChangeRow key={change.itemId} change={change} language={i18n.language} />
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            </div>
          ))}
        </>
      )}
    </section>
  );
}

function SummaryCards({
  summary,
  t,
}: {
  summary: MenuPreviewSummary;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const cells: Array<{ key: string; value: number | string; tone?: 'danger' | 'warning' }> = [
    { key: 'summaryBranches', value: summary.branches },
    { key: 'summaryItems', value: summary.items },
    { key: 'summaryAdded', value: summary.added },
    { key: 'summaryWithdrawn', value: summary.withdrawn, tone: summary.withdrawn > 0 ? 'warning' : undefined },
    { key: 'summaryRepriced', value: summary.repriced },
    { key: 'summaryChannelsChanged', value: summary.channelsChanged },
    { key: 'summaryReordered', value: summary.reordered },
    { key: 'summaryWarnings', value: summary.warnings, tone: summary.warnings > 0 ? 'danger' : undefined },
  ];

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
      {cells.map((cell) => (
        <div key={cell.key} className="rounded-lg border p-3">
          <p className="text-xs text-ink-muted">{t(`menuGovernance.preview.${cell.key}`)}</p>
          <p
            className={
              'text-lg font-semibold tabular-nums ' +
              (cell.tone === 'danger'
                ? 'text-red-600 dark:text-red-400'
                : cell.tone === 'warning'
                  ? 'text-amber-600 dark:text-amber-400'
                  : '')
            }
          >
            {cell.value}
          </p>
        </div>
      ))}
      {summary.largestPriceMovePercent && (
        <div className="rounded-lg border p-3">
          <p className="text-xs text-ink-muted">{t('menuGovernance.preview.summaryLargestMove')}</p>
          <p className="text-lg font-semibold tabular-nums">{summary.largestPriceMovePercent}%</p>
        </div>
      )}
    </div>
  );
}

function ChangeRow({ change, language }: { change: MenuPreviewChange; language: string }) {
  const { t } = useTranslation();
  const money = (amount: string) => formatCurrency(amount, change.currency, language);

  return (
    <>
      <TR>
        <TD className="font-medium">{change.itemName}</TD>
        <TD>
          <div className="flex flex-wrap gap-1">
            {change.kinds.map((kind) => (
              <Badge key={kind} tone={KIND_TONE[kind]}>
                {t(`menuGovernance.preview.kind${kind}`)}
              </Badge>
            ))}
          </div>
        </TD>
        <TD className="tabular-nums">
          <Side side={change.before} money={money} t={t} />
        </TD>
        <TD className="px-0 text-ink-muted">
          <Icon as={ArrowRight} size="md" />
        </TD>
        <TD className="tabular-nums">
          <Side side={change.after} money={money} t={t} />
        </TD>
        <TD className="text-end tabular-nums">
          {change.pricePercent ? (
            <span
              className={
                change.pricePercent.startsWith('-')
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-green-600 dark:text-green-400'
              }
            >
              {change.pricePercent}%
            </span>
          ) : (
            '—'
          )}
        </TD>
      </TR>
      {change.warnings.length > 0 && (
        <TR>
          <TD colSpan={6} className="pt-0">
            <div className="space-y-1">
              {change.warnings.map((warning, i) => (
                <p
                  key={i}
                  className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400"
                >
                  <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {warningSentence(warning, change, t)}
                </p>
              ))}
            </div>
          </TD>
        </TR>
      )}
    </>
  );
}

function Side({
  side,
  money,
  t,
}: {
  side: MenuPreviewChange['before'];
  money: (amount: string) => string;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  if (!side.carried) {
    return <span className="text-ink-muted">{t('menuGovernance.preview.notCarried')}</span>;
  }
  return (
    <span>
      {money(side.effectivePrice)}
      {side.price === null && (
        <span className="ms-1 text-xs text-ink-muted">{t('menuGovernance.preview.inheritedTag')}</span>
      )}
    </span>
  );
}
